/**
 * Base API client with project scoping and error handling.
 */

import { getCurrentProject } from '../app.js'
import { getAuthToken, handleUnauthorized } from '../auth/auth.js'

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiClientError'
  }
}

const ENVELOPE_HEADER = 'X-Envelope'
const ENVELOPE_SENTINEL = 'grizzly-web-v1'

function isEnvelopeWrapped(res: Response): boolean {
  return res.headers.get(ENVELOPE_HEADER) === ENVELOPE_SENTINEL
}

/**
 * Query-string parameters — and explicitly NOT fetch options.
 *
 * ── Why the `never` fields are here ──────────────────────────────────────
 * `apiFetch(path, params)` looks exactly like `fetch(url, init)`, and the
 * device-pairing page was written as though it were:
 *
 *   apiFetch('/api/auth/device/approve', { method: 'POST', body: JSON... })
 *
 * Every value was a string, so it satisfied `Record<string, string>` and the
 * typecheck passed. At runtime it issued a GET and appended both keys to the
 * URL — so the request 404'd AND put a live OAuth authorization code into a
 * query string, where it lands in browser history, Referer headers and access
 * logs. The file's own comment said a credential must never pass through
 * there.
 *
 * Declaring these three as `never` turns that specific mistake into a
 * compile error rather than a comment nobody reads at the call site.
 */
export type QueryParams = Record<string, string | number | boolean | undefined> & {
  method?: never
  body?: never
  headers?: never
}

/** The headers every call shares: tenant scope and bearer token. */
function commonHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  const projectId = getCurrentProject()
  if (projectId) headers['X-Project-Id'] = projectId
  const token = getAuthToken()
  if (token) headers.Authorization = `Bearer ${token}`
  return headers
}

/**
 * A 401 means the session is over — end it here, once, for the whole app.
 *
 * Called immediately after every fetch, BEFORE the response body is read and
 * before any error is thrown, so it fires regardless of which of the several
 * throw paths below the response takes.
 *
 * Without this the app had no reaction to its token expiring: each panel
 * surfaced its own "Invalid or expired token" and the shell stayed up, so a
 * refresh reproduced it and only clearing site data reached the login page.
 *
 * The call still throws afterwards. Callers keep their existing error handling;
 * this only guarantees the session is torn down and the login page mounts.
 */
function guardResponse(res: Response): void {
  if (res.status === 401) handleUnauthorized()
}

/** Unwrap the standard envelope, or return the raw body when it is not wrapped. */
async function readBody<T>(res: Response): Promise<T> {
  const rawJson = (await res.json()) as unknown
  if (!isEnvelopeWrapped(res)) return rawJson as T
  const json = rawJson as { data?: T; error?: { code: string; message: string } }
  if (json.error) throw new ApiClientError(res.status, json.error.message)
  return json.data as T
}

/**
 * A request that CHANGES something: a real verb, a JSON body, nothing in the URL.
 *
 * Separate from `apiFetch` because the SPA was read-only until device pairing
 * arrived, so the only way to mutate was a hand-rolled `fetch` — which is how
 * the pairing page ended up not mutating at all.
 */
export async function apiMutate<T>(
  path: string,
  init: { method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown },
): Promise<T> {
  if (!path.startsWith('/')) {
    throw new ApiClientError(0, `apiMutate: path must be relative, got: ${path.substring(0, 50)}`)
  }
  const res = await fetch(new URL(path, location.origin).toString(), {
    method: init.method,
    headers: { ...commonHeaders(), 'Content-Type': 'application/json' },
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  })
  guardResponse(res)
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    const message = text ? text.substring(0, 200).replace(/[\r\n]/g, ' ') : `HTTP ${res.status}`
    throw new ApiClientError(res.status, message)
  }
  return readBody<T>(res)
}

export async function apiFetch<T>(path: string, params?: QueryParams): Promise<T> {
  if (!path.startsWith('/')) {
    throw new ApiClientError(0, `apiFetch: path must be relative, got: ${path.substring(0, 50)}`)
  }
  const url = new URL(path, location.origin)
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) {
        url.searchParams.set(k, String(v))
      }
    }
  }

  const headers: Record<string, string> = {}
  // Tenant id goes in the `X-Project-Id` header — that's the contract
  // the server's projectIdMiddleware enforces on /api/v2/kb|work|fleet.
  // Kept out of the query string so the tenant id doesn't leak via
  // Referer, browser history, or access logs. Skipped for requests that
  // fire before a project is picked (e.g. the initial /api/v2/projects
  // call from initProjects); those routes don't require the header.
  const projectId = getCurrentProject()
  if (projectId) {
    headers['X-Project-Id'] = projectId
  }
  const token = getAuthToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const res = await fetch(url.toString(), { headers })
  guardResponse(res)
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    const message = body ? body.substring(0, 200).replace(/[\r\n]/g, ' ') : `HTTP ${res.status}`
    throw new ApiClientError(res.status, message)
  }
  const rawJson = (await res.json()) as unknown
  if (isEnvelopeWrapped(res)) {
    const json = rawJson as { data?: T; error?: { code: string; message: string } }
    if (json.error) {
      throw new ApiClientError(res.status, json.error.message)
    }
    if (json.data === undefined) {
      throw new ApiClientError(res.status, 'envelope response missing data field')
    }
    return json.data
  }
  return rawJson as T
}

export async function apiFetchWithMeta<T>(
  path: string,
  params?: QueryParams,
): Promise<{
  data: T
  meta?: { page?: number; page_size?: number; total?: number; has_more?: boolean }
}> {
  if (!path.startsWith('/')) {
    throw new ApiClientError(
      0,
      `apiFetchWithMeta: path must be relative, got: ${path.substring(0, 50)}`,
    )
  }
  const url = new URL(path, location.origin)
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) {
        url.searchParams.set(k, String(v))
      }
    }
  }

  const headers: Record<string, string> = {}
  // Tenant id goes in the `X-Project-Id` header — see apiFetch() above
  // for the rationale.
  const projectId = getCurrentProject()
  if (projectId) {
    headers['X-Project-Id'] = projectId
  }
  const token = getAuthToken()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  const res = await fetch(url.toString(), { headers })
  guardResponse(res)
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    const message = body ? body.substring(0, 200).replace(/[\r\n]/g, ' ') : `HTTP ${res.status}`
    throw new ApiClientError(res.status, message)
  }
  const rawJson = (await res.json()) as unknown
  if (isEnvelopeWrapped(res)) {
    const json = rawJson as {
      data?: T
      meta?: { page?: number; page_size?: number; total?: number; has_more?: boolean }
      error?: { code: string; message: string }
    }
    if (json.error) {
      throw new ApiClientError(res.status, json.error.message)
    }
    if (json.data === undefined) {
      throw new ApiClientError(res.status, 'envelope response missing data field')
    }
    return { data: json.data, meta: json.meta }
  }
  interface PyListShape {
    items?: unknown[]
    entries?: unknown[]
    matches?: unknown[]
    total?: number
    page?: number
    page_size?: number
    has_more?: boolean
  }
  const py = rawJson as PyListShape
  const rows = py.items ?? py.entries ?? py.matches
  if (rows === undefined) {
    throw new ApiClientError(
      res.status,
      'unexpected bare payload shape: missing items/entries/matches at top level',
    )
  }
  return {
    data: rows as T,
    meta: {
      page: py.page,
      page_size: py.page_size,
      total: py.total,
      has_more: py.has_more,
    },
  }
}

export function createEventSource(
  path: string,
  params?: Record<string, string | number | boolean | undefined>,
): EventSource {
  if (!path.startsWith('/')) {
    throw new ApiClientError(
      0,
      `createEventSource: path must be relative, got: ${path.substring(0, 50)}`,
    )
  }
  const url = new URL(path, location.origin)
  // EventSource cannot attach custom headers, so tenant id rides on the
  // query string here. The server's projectIdMiddleware reads either
  // `X-Project-Id` (preferred) or `project_id` — this path uses the
  // latter out of necessity. Same charset constraint applies server-side.
  const projectId = getCurrentProject()
  if (projectId) {
    url.searchParams.set('project_id', projectId)
  }
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) {
        url.searchParams.set(k, String(v))
      }
    }
  }
  const token = getAuthToken()
  if (token) {
    url.searchParams.set('access_token', token)
  }
  return new EventSource(url.toString())
}
